/* ===== game/boss-water-art.ts — 바다 보스의 물 그림(부서지는 파도 · 차오르는 물) ===== */
import { TAU } from '../../engine/core/math.js';
/* ★ 파란 덩어리 하나로 그리지 않는다(점검판 2026-11-03 "바다 보스 파도를 더 화려하고 사실적으로 · 더 밝고 또렷하게").
   물은 깊이에 따라 색이 바뀌고(마루는 빛이 비쳐 청록 · 밑은 짙은 남색), 앞머리가 말리며 속이 빈 굴(튜브)이 생기고,
   마루 · 입술에 거품이 일고 물보라가 앞으로 튄다. 거품은 둥근 점 하나가 아니라 크기가 다른 덩이가 겹친 띠다. 사연: docs/code-history.md#h261 */

type C2 = CanvasRenderingContext2D;
const hs = (s: number, i: number) => { const v = Math.sin(s * 127.1 + i * 311.7) * 43758.5453; return v - Math.floor(v); };

/** 거품 띠 — 점 (x, y) 들을 따라 크기가 다른 흰 덩이를 겹친다 */
function foam(c: C2, sd: number, pts: number[], r: number, T: number, a = 1, line = true) {
  if (line && pts.length >= 4) {                                         // 밑 띠 — 덩이 사이가 비어 진주알처럼 줄지어 보이지 않게
    c.lineCap = 'round'; c.lineJoin = 'round';
    c.beginPath(); c.moveTo(pts[0], pts[1]); for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
    c.strokeStyle = `rgba(225,245,250,${0.55 * a})`; c.lineWidth = r * 1.7; c.stroke();
  }
  for (let i = 0; i < pts.length; i += 2) {
    const k = i / 2; if (hs(sd, k + 700) < 0.3) continue;
    for (let j = 0; j < 2; j++) {                                        // 한 자리에 크고 작은 덩이 둘
      const rr = r * (0.3 + hs(sd, k * 3 + j) * 0.75) * (1 + Math.sin(T * 9 + k * 1.7 + j) * 0.15);
      const x = pts[i] + (hs(sd, k + j * 50) - 0.5) * r * 1.6 + Math.sin(T * 6 + k) * 1.2, y = pts[i + 1] + (hs(sd, k + j * 60) - 0.5) * r + Math.cos(T * 7 + k * 1.3) * 1.2;
      c.fillStyle = `rgba(205,232,242,${0.7 * a})`; c.beginPath(); c.arc(x, y + rr * 0.3, rr, 0, TAU); c.fill();
      c.fillStyle = `rgba(255,255,255,${0.85 * a})`; c.beginPath(); c.arc(x - rr * 0.2, y - rr * 0.1, rr * 0.7, 0, TAU); c.fill();
    }
  }
}

/** 부서지는 파도 — dir 쪽으로 달린다. grounded 가 아니면(높은 벽) 밑이 비어 그 아래로 지나간다 */
export function breaker(c: C2, sd: number, x: number, top: number, bot: number, w: number, dir: number, grounded: boolean, T: number) {
  const H = bot - top, W = Math.max(w, H * 0.9), d = dir;
  const X = (u: number) => x + d * W * u;
  const wob = Math.sin(T * 5 + sd) * H * 0.02;
  const body = () => {
    c.beginPath();
    c.moveTo(X(-0.9), bot);
    c.bezierCurveTo(X(-0.55), bot - H * 0.15, X(-0.3), top + H * 0.15, X(0.05), top + wob);   // 뒷비탈 → 마루
    c.bezierCurveTo(X(0.3), top - H * 0.04, X(0.5), top + H * 0.08, X(0.52), top + H * 0.22);   // 앞으로 말리는 입술
    c.bezierCurveTo(X(0.53), top + H * 0.32, X(0.44), top + H * 0.38, X(0.38), top + H * 0.34); // 떨어지는 입술 끝
    c.bezierCurveTo(X(0.3), top + H * 0.3, X(0.2), top + H * 0.38, X(0.2), top + H * 0.5);      // 굴 안쪽 벽
    c.bezierCurveTo(X(0.22), top + H * 0.72, X(0.36), bot - H * 0.08, X(0.48), bot);           // 앞 얼굴
    c.closePath();
  };
  body();
  const g = c.createLinearGradient(0, top, 0, bot);
  g.addColorStop(0, 'rgba(150,236,240,.95)'); g.addColorStop(0.3, 'rgba(56,178,206,.92)'); g.addColorStop(0.75, 'rgba(22,108,160,.92)'); g.addColorStop(1, 'rgba(14,64,118,.9)');
  c.fillStyle = g; c.fill();
  c.save(); body(); c.clip();
  const tr = c.createRadialGradient(X(-0.05), top + H * 0.25, 0, X(-0.05), top + H * 0.25, H * 0.6);   // 마루 밑 — 빛이 물을 뚫고 비친다
  tr.addColorStop(0, 'rgba(170,255,240,.55)'); tr.addColorStop(1, 'rgba(170,255,240,0)');
  c.fillStyle = tr; c.fillRect(X(-1) - W, top - 10, W * 3, H + 20);
  const tube = c.createRadialGradient(X(0.36), top + H * 0.36, 0, X(0.36), top + H * 0.36, H * 0.22);   // 말린 굴 속 그늘
  tube.addColorStop(0, 'rgba(6,40,80,.75)'); tube.addColorStop(1, 'rgba(6,40,80,0)');
  c.fillStyle = tube; c.fillRect(X(0.1) - W, top, W * 2, H * 0.7);
  c.strokeStyle = 'rgba(255,255,255,.35)'; c.lineWidth = 1.2;           // 앞 얼굴을 흘러내리는 물결 줄
  for (let i = 0; i < 6; i++) {
    const u = 0.22 + hs(sd, i) * 0.22, ph = (T * 1.6 + hs(sd, i + 9)) % 1, y0 = top + H * (0.4 + ph * 0.5);
    c.beginPath(); c.moveTo(X(u), y0); c.quadraticCurveTo(X(u + 0.04), y0 + H * 0.1, X(u + 0.1), y0 + H * 0.16); c.stroke();
  }
  c.strokeStyle = 'rgba(200,250,255,.3)';                                // 뒷비탈 잔물결
  for (let i = 0; i < 5; i++) { const u = -0.75 + i * 0.16, yy = bot - H * (0.12 + i * 0.16) + Math.sin(T * 4 + i) * 2; c.beginPath(); c.moveTo(X(u), yy); c.quadraticCurveTo(X(u + 0.08), yy - 4, X(u + 0.16), yy); c.stroke(); }
  c.restore();
  c.strokeStyle = 'rgba(230,252,255,.9)'; c.lineWidth = 2;               // 마루 윤곽 빛
  c.beginPath(); c.moveTo(X(-0.55), top + H * 0.42); c.bezierCurveTo(X(-0.3), top + H * 0.15, X(-0.15), top + H * 0.02, X(0.05), top + wob); c.stroke();
  const crest: number[] = [];                                            // 마루 → 입술 끝 거품
  for (let i = 0; i <= 12; i++) {
    const u = i / 12, a = -0.25 + u * 0.75, e = top + (u < 0.45 ? -Math.sin(u / 0.45 * Math.PI * 0.5) * H * 0.02 : H * 0.3 * ((u - 0.45) / 0.55) ** 1.6);
    crest.push(X(a), e + H * 0.02);
  }
  foam(c, sd, crest, Math.max(3, H * 0.055), T);
  for (let i = 0; i < 18; i++) {                                         // 물보라 — 입술에서 앞 · 위로 튄다
    const u = (T * 1.8 + hs(sd, i + 30)) % 1, vx = (0.25 + hs(sd, i + 40) * 0.6) * W, vy = (0.3 + hs(sd, i + 50) * 0.5) * H;
    const px = X(0.45) + d * vx * u, py = top + H * 0.2 - vy * u + H * 1.1 * u * u, r = 1 + hs(sd, i + 60) * 2.6;
    c.fillStyle = `rgba(240,252,255,${0.9 * (1 - u)})`; c.beginPath(); c.arc(px, py, r * (1 - u * 0.4), 0, TAU); c.fill();
  }
  if (grounded) {                                                        // 밑동 — 부서진 물이 뒤엉켜 하얗게 끓는다
    const base: number[] = []; for (let i = 0; i <= 14; i++) base.push(X(-0.9 + i / 14 * 1.5), bot - 2 - Math.abs(Math.sin(T * 8 + i)) * 3);
    foam(c, sd + 3, base, Math.max(3, H * 0.06), T, 0.85);
  } else {                                                               // 높은 파도 — 밑 가장자리에서 물발이 흩날린다(그 아래가 빈 자리)
    const edge: number[] = []; for (let i = 0; i <= 10; i++) edge.push(X(-0.6 + i / 10 * 1.1), bot);
    foam(c, sd + 5, edge, Math.max(2.5, H * 0.03), T, 0.8);
    for (let i = 0; i < 12; i++) { const u = (T * 2 + hs(sd, i + 70)) % 1, px = X(-0.6 + hs(sd, i + 80) * 1.1); c.fillStyle = `rgba(220,245,255,${0.7 * (1 - u)})`; c.fillRect(px, bot + u * 40, 1.5, 4); }
  }
}

/** 차오르는 물 — 화면 너비. y = 수면, bot = 물 밑 */
export function risingWater(c: C2, sd: number, X0: number, X1: number, y: number, bot: number, T: number, taper = 0) {
  const W = X1 - X0;
  const surf = (x: number) => {                                          // taper 칸만큼 양 끝이 바닥으로 둥글게 내려앉는다(네모 상자 끝 대신)
    const e = taper ? Math.min(x - X0, X1 - x) / taper : 1, k = e < 1 ? (1 - Math.max(0, e)) ** 2 : 0;
    return y + Math.sin(x * 0.022 + T * 2.4) * 4 + Math.sin(x * 0.061 - T * 3.7) * 2 + k * (bot - y);
  };
  const shape = () => { c.beginPath(); c.moveTo(X0, bot); for (let x = X0; x <= X1; x += 12) c.lineTo(x, surf(x)); c.lineTo(X1, surf(X1)); c.lineTo(X1, bot); c.closePath(); };
  shape();
  const g = c.createLinearGradient(0, y, 0, bot);
  g.addColorStop(0, 'rgba(120,220,236,.42)'); g.addColorStop(0.25, 'rgba(46,160,205,.46)'); g.addColorStop(1, 'rgba(16,72,130,.66)');   // 잠긴 몸 · 발판이 비쳐 보이게
  c.fillStyle = g; c.fill();
  c.save(); shape(); c.clip();
  c.globalCompositeOperation = 'lighter';
  c.strokeStyle = 'rgba(140,240,255,.16)'; c.lineWidth = 2;              // 물속 빛 그물(카우스틱)
  for (let r = 0; r < 3; r++) for (let x = X0 - 40; x < X1 + 40; x += 46) {
    const yy = y + 14 + r * 22, o = Math.sin(T * 1.7 + x * 0.05 + r) * 10;
    c.beginPath(); c.moveTo(x + o, yy); c.quadraticCurveTo(x + 23 + o, yy + 8 * Math.sin(T * 2 + x), x + 46 + o, yy); c.stroke();
  }
  for (let i = 0; i < 6; i++) {                                          // 비스듬히 내리꽂는 빛줄기
    const x0 = X0 + (hs(sd, i) * W + T * 18) % (W + 200) - 100, gg = c.createLinearGradient(0, y, 0, y + 120);
    gg.addColorStop(0, 'rgba(180,250,255,.18)'); gg.addColorStop(1, 'rgba(180,250,255,0)');
    c.fillStyle = gg; c.beginPath(); c.moveTo(x0, y); c.lineTo(x0 + 22, y); c.lineTo(x0 + 52, y + 120); c.lineTo(x0 + 34, y + 120); c.closePath(); c.fill();
  }
  c.globalCompositeOperation = 'source-over';
  c.fillStyle = 'rgba(230,250,255,.7)';                                  // 오르는 기포
  for (let i = 0; i < 24; i++) { const u = (T * 0.5 + hs(sd, i + 20)) % 1, px = X0 + hs(sd, i + 30) * W + Math.sin(T * 3 + i) * 4, py = bot - u * (bot - y); c.beginPath(); c.arc(px, py, 1 + hs(sd, i + 40) * 1.8, 0, TAU); c.fill(); }
  c.restore();
  const sh = c.createLinearGradient(0, y - 4, 0, y + 10);               // 수면 반짝 띠
  sh.addColorStop(0, 'rgba(255,255,255,0)'); sh.addColorStop(0.4, 'rgba(230,252,255,.55)'); sh.addColorStop(1, 'rgba(255,255,255,0)');
  if (!taper) { c.fillStyle = sh; c.fillRect(X0, y - 6, W, 16); }
  c.strokeStyle = 'rgba(245,253,255,.95)'; c.lineWidth = 2; c.beginPath();
  for (let x = X0; x <= X1; x += 12) { if (x > X0) c.lineTo(x, surf(x)); else c.moveTo(x, surf(x)); } c.stroke();
  const fm: number[] = [];                                               // 끊긴 거품 조각
  for (let i = 0; i < 26; i++) { const x = X0 + ((hs(sd, i + 50) * W + T * 30 * (hs(sd, i + 60) - 0.5)) % W + W) % W; if (Math.sin(T * 1.3 + i * 2.3) > 0.1) fm.push(x, surf(x)); }
  foam(c, sd, fm, 2.6, T, 0.8, false);
  c.fillStyle = 'rgba(255,255,255,.9)';                                  // 수면의 해 반짝임
  for (let i = 0; i < 16; i++) { const x = X0 + hs(sd, i + 90) * W, tw = Math.max(0, Math.sin(T * 6 + i * 1.9)); if (tw > 0.4) c.fillRect(x - tw * 4, surf(x) + 2, tw * 8, 1.4); }
}

/** 소용돌이 — 바닥에 누운 물 깔때기: 바깥은 밝은 청록 물결 띠, 안으로 갈수록 짙어져 가운데가 빨려 내려간다. 띠마다 거품 팔이 감긴다 */
export function whirlpool(c: C2, sd: number, x: number, y: number, r: number, f: number, T: number) {
  c.save(); c.translate(x, y); c.scale(1, 0.38); c.globalAlpha = f;
  const g = c.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, 'rgba(4,24,52,.95)'); g.addColorStop(0.25, 'rgba(14,70,120,.85)'); g.addColorStop(0.7, 'rgba(40,150,200,.55)'); g.addColorStop(1, 'rgba(90,200,230,0)');
  c.fillStyle = g; c.beginPath(); c.arc(0, 0, r, 0, TAU); c.fill();
  for (let i = 0; i < 6; i++) {                                          // 거품 팔 — 안으로 감겨 들어간다(굵기가 안쪽으로 가늘어진다)
    const a0 = -T * 2.4 + i * TAU / 6;
    for (let j = 0; j < 26; j++) {
      const u = j / 26, a = a0 + u * 4.2, rr = r * (0.95 - u * 0.85), w = (1 - u) * r * 0.05 + 1;
      const px = Math.cos(a) * rr, py = Math.sin(a) * rr;
      c.fillStyle = `rgba(235,250,255,${(0.75 - u * 0.5) * (0.6 + 0.4 * hs(sd, i * 30 + j))})`;
      c.beginPath(); c.ellipse(px, py, w * 1.6, w, a + Math.PI / 2, 0, TAU); c.fill();
    }
  }
  c.strokeStyle = 'rgba(160,235,250,.35)'; c.lineWidth = 3;              // 물결 띠 — 흐름에 따라 도는 반짝임(끊긴 호 · 고리 아님)
  for (let i = 0; i < 9; i++) { const rr = r * (0.3 + i * 0.075), a = -T * (3.2 - i * 0.2) + hs(sd, i) * TAU; c.beginPath(); c.arc(0, 0, rr, a, a + 0.9 + hs(sd, i + 9)); c.stroke(); }
  c.restore(); c.globalAlpha = 1;
  for (let i = 0; i < 10; i++) {                                         // 가장자리 물보라
    const u = (T * 1.4 + hs(sd, i + 40)) % 1, a = hs(sd, i + 50) * TAU - T, px = x + Math.cos(a) * r * 0.9, py = y + Math.sin(a) * r * 0.34 - u * 26;
    c.fillStyle = `rgba(235,250,255,${0.8 * (1 - u) * f})`; c.beginPath(); c.arc(px, py, 1.5 + u * 2, 0, TAU); c.fill();
  }
}

/** 물줄기 광선 — 회전 좌표(+x). 꼬이는 물기둥 · 겉은 흩어지는 물보라 · 속은 흰 압력심 */
export function waterJet(c: C2, sd: number, len: number, w: number, fade: number, T: number) {
  const op = c.globalCompositeOperation;
  const tw = (s: number, k: number) => Math.sin(s * 0.05 - T * 22 + k * 2.1) * w * 0.18 * Math.min(1, s / 60);
  for (let k = 0; k < 3; k++) {                                          // 꼬인 가닥 셋
    c.beginPath(); c.moveTo(0, 0); for (let s = 10; s <= len; s += 10) c.lineTo(s, tw(s, k));
    c.lineCap = 'round'; c.strokeStyle = ['rgba(20,100,170,.7)', 'rgba(60,170,220,.75)', 'rgba(150,225,250,.8)'][k]; c.lineWidth = w * (1.3 - k * 0.3) * fade; c.stroke();
  }
  c.globalCompositeOperation = 'lighter';
  c.beginPath(); c.moveTo(0, 0); for (let s = 10; s <= len; s += 10) c.lineTo(s, tw(s, 1) * 0.5);
  c.strokeStyle = `rgba(255,255,255,${0.85 * fade})`; c.lineWidth = Math.max(2, w * 0.2); c.stroke();
  c.globalCompositeOperation = op;
  for (let i = 0; i < 30; i++) {                                         // 겉으로 흩어지는 물방울
    const s = (hs(sd, i) * len + T * 520) % len, side = hs(sd, i + 40) < 0.5 ? -1 : 1, off = side * w * (0.5 + hs(sd, i + 50) * 0.6) * (0.5 + s / len);
    c.fillStyle = `rgba(225,248,255,${0.85 * fade})`; c.beginPath(); c.arc(s, tw(s, 0) + off, 1 + hs(sd, i + 60) * 2.2, 0, TAU); c.fill();
  }
  for (let i = 0; i < 8; i++) {                                          // 끝 — 부딪혀 튀는 물보라
    const u = (T * 3 + hs(sd, i + 80)) % 1, a = (hs(sd, i + 90) - 0.5) * 2.4, d = u * w * 2.6;
    c.fillStyle = `rgba(235,250,255,${0.8 * (1 - u) * fade})`; c.beginPath(); c.arc(len + Math.cos(a) * d - w * 0.5, Math.sin(a) * d, 2 + u * 3, 0, TAU); c.fill();
  }
}

/** 공기 분출 — 잠수복 밸브에서 뿜는 압축 공기: 원뿔로 퍼지는 기포 떼 · 압력에 일렁이는 흰 김 */
export function bubbleJet(c: C2, sd: number, len: number, w: number, fade: number, T: number) {
  const g = c.createLinearGradient(0, 0, len, 0); g.addColorStop(0, `rgba(240,252,255,${0.85 * fade})`); g.addColorStop(0.5, `rgba(170,225,245,${0.4 * fade})`); g.addColorStop(1, 'rgba(170,225,245,0)');
  c.fillStyle = g; c.beginPath(); c.moveTo(0, -w * 0.2); c.lineTo(len, -w * 1.1); c.lineTo(len, w * 1.1); c.lineTo(0, w * 0.2); c.closePath(); c.fill();
  for (let i = 0; i < 70; i++) {
    const u = (hs(sd, i) + T * (1.6 + hs(sd, i + 9))) % 1, s = u * len, spread = (hs(sd, i + 20) - 0.5) * 2 * w * (0.2 + u * 0.9) + Math.sin(T * 9 + i) * 2;
    const r = (2.5 + hs(sd, i + 30) * 6) * (0.6 + u * 0.9);
    c.strokeStyle = `rgba(235,252,255,${0.9 * fade * (1 - u * 0.5)})`; c.lineWidth = 1.2;
    c.fillStyle = `rgba(150,215,240,${0.4 * fade})`;
    c.beginPath(); c.arc(s, spread, r, 0, TAU); c.fill(); c.stroke();
    c.fillStyle = `rgba(255,255,255,${0.9 * fade})`; c.fillRect(s - r * 0.45, spread - r * 0.5, Math.max(1, r * 0.35), Math.max(1, r * 0.35));
  }
}

/** 쇠사슬 — 고리가 번갈아 서고 눕는다(명암 · 녹). sag 는 처짐(px) */
export function ironChain(c: C2, x0: number, y0: number, x1: number, y1: number, sag: number, T: number) {
  const L = Math.hypot(x1 - x0, y1 - y0), n = Math.max(2, Math.floor(L / 11));
  for (let i = 0; i <= n; i++) {
    const u = i / n, x = x0 + (x1 - x0) * u, y = y0 + (y1 - y0) * u + Math.sin(u * Math.PI) * sag;
    const u2 = Math.min(1, u + 0.01), ang = Math.atan2((y1 - y0) + Math.cos(u2 * Math.PI) * sag * Math.PI, x1 - x0);
    c.save(); c.translate(x, y); c.rotate(ang);
    if (i % 2) { c.fillStyle = '#2a3036'; c.fillRect(-7, -1.6, 14, 3.2); c.fillStyle = '#7a8690'; c.fillRect(-6, -1.6, 12, 1.2); }   // 누운 고리 — 옆에서 본 막대
    else {
      c.lineWidth = 3.4; c.strokeStyle = '#2a3036'; c.beginPath(); c.ellipse(0, 0, 7.5, 4.5, 0, 0, TAU); c.stroke();
      c.lineWidth = 1.6; c.strokeStyle = '#9aa6b0'; c.beginPath(); c.ellipse(0, -0.6, 7, 4, 0, Math.PI * 1.05, Math.PI * 1.95); c.stroke();   // 윗날 빛
      c.strokeStyle = 'rgba(150,90,50,.7)'; c.beginPath(); c.ellipse(0, 0.6, 7, 4, 0, 0.3, 1.2); c.stroke();                               // 녹
    }
    c.restore();
  }
}

/** 쇠닻 — 고리 · 가로대 · 자루 · 두 갈고리(삽날). s 는 크기 배수 */
export function ironAnchor(c: C2, x: number, y: number, rot: number, s = 1) {
  c.save(); c.translate(x, y); c.rotate(rot); c.scale(s, s);
  const metal = (path: () => void, w: number) => {
    path(); c.strokeStyle = '#1a1e24'; c.lineWidth = w + 2.6; c.lineCap = 'round'; c.stroke();
    path(); c.strokeStyle = '#4a5560'; c.lineWidth = w; c.stroke();
    c.save(); c.translate(-w * 0.18, -w * 0.18); path(); c.strokeStyle = 'rgba(190,205,215,.75)'; c.lineWidth = w * 0.3; c.stroke(); c.restore();
  };
  metal(() => { c.beginPath(); c.arc(0, -24, 5, 0, TAU); }, 3);                                     // 고리
  metal(() => { c.beginPath(); c.moveTo(-13, -15); c.lineTo(13, -15); }, 4.5);                      // 가로대
  metal(() => { c.beginPath(); c.moveTo(0, -19); c.lineTo(0, 18); }, 6);                            // 자루
  metal(() => { c.beginPath(); c.arc(0, 2, 18, 0.12 * Math.PI, 0.88 * Math.PI); }, 5.5);            // 팔
  for (const sx of [-1, 1]) {                                                                       // 삽날 갈고리
    const ex = Math.cos(sx > 0 ? 0.12 * Math.PI : 0.88 * Math.PI) * 18, ey = 2 + Math.sin(0.12 * Math.PI) * 18;
    c.beginPath(); c.moveTo(ex, ey); c.lineTo(ex + sx * 7, ey - 13); c.lineTo(ex - sx * 3, ey - 4); c.closePath();
    c.fillStyle = '#4a5560'; c.fill(); c.strokeStyle = '#1a1e24'; c.lineWidth = 1.6; c.stroke();
  }
  c.fillStyle = 'rgba(140,80,40,.55)'; c.beginPath(); c.arc(-1, 8, 3, 0, TAU); c.arc(5, 17, 2, 0, TAU); c.fill();   // 녹 · 따개비
  c.fillStyle = '#d8d0b8'; c.beginPath(); c.arc(-9, 14, 2, 0, TAU); c.arc(-6, 17, 1.5, 0, TAU); c.fill();
  c.restore();
}
